import { selectFullAppConfig } from '@src/providers/AppConfigRedux/selectors';
import { useAppStateSelector } from '@src/providers/AppStateProvider';
import {
  BASE_NAV_PATHS,
  canAccessOperationsAsDealer,
  LEGACY_OPERATIONS_NAV,
  LEGACY_PORTFOLIO_ENGINEERING_NAV,
  PORTFOLIO_LEGACY_FULL_NAV,
  PORTFOLIO_LITE_NAV,
  PORTFOLIO_NAV,
  type NavigationItem,
} from '@src/tokens/navigationPaths';
import type { ACTION } from '@talos/kyoko';
import { notEmpty } from '@talos/kyoko';
import { useMemo } from 'react';
import { useFeatureFlag, type FeatureFlags } from './useFeatureFlag';
import { useRoleAuth } from './useRoleAuth';
import { useSpacesNavItems } from './useSpacesNavItems';

const FIXED_NAV_ITEMS_BY_LAYOUT_SECTION = {
  trading: BASE_NAV_PATHS.find(item => item.path === '/trading')?.children ?? [],
  monitoring: BASE_NAV_PATHS.find(item => item.path === '/monitoring')?.children ?? [],
  intel: BASE_NAV_PATHS.find(item => item.path === '/intel')?.children ?? [],
  dealer: BASE_NAV_PATHS.find(item => item.path === '/dealer')?.children ?? [],
} satisfies Record<'trading' | 'monitoring' | 'intel' | 'dealer', NavigationItem[]>;

/**
 * Filters navigation items by permission and feature flags.
 * @param items - The navigation items to filter.
 * @param isAuthorized - A function that checks if a user is authorized to access a given permission.
 * @param flags - The feature flags to check.
 * @returns The filtered navigation items.
 */
function filterNavigationItemsByPermission(
  items: NavigationItem[],
  isAuthorized: (permission: ACTION) => boolean,
  flags: FeatureFlags
): NavigationItem[] {
  return items
    .filter(item => {
      // Check visibility callback
      if (item.isVisible && !item.isVisible(flags, isAuthorized)) {
        return false;
      }

      return true;
    })
    .map(item => {
      if (!item.children) {
        return item;
      }

      const filteredChildren = filterNavigationItemsByPermission(item.children, isAuthorized, flags);

      if (filteredChildren.length === 0) {
        if (item.keepWhenEmpty) {
          const { children: _dropped, ...rest } = item;
          return rest;
        }
        return null;
      }

      return {
        ...item,
        children: filteredChildren,
      };
    })
    .filter(notEmpty);
}

/**
 * A hook to get the navigation items, filtered by permission and feature flags.
 * @returns An object containing all the navigation items, top navigation items, bottom navigation items, and user menu items.
 */
export function useNavigationItems() {
  const { isAuthorized } = useRoleAuth();
  const flags = useFeatureFlag();

  const appConfig = useAppStateSelector(selectFullAppConfig);
  const { enablePMSPENavigationMigration, enablePortfolioManagementSystem } = flags;
  // Don't rely on PortfolioSettingsContext here, use the appConfig directly
  const enablePMSFull = appConfig.enablePMSLayout && enablePortfolioManagementSystem;

  const tradingNav = useSpacesNavItems('trading', FIXED_NAV_ITEMS_BY_LAYOUT_SECTION.trading);
  const monitoringNav = useSpacesNavItems('monitoring', FIXED_NAV_ITEMS_BY_LAYOUT_SECTION.monitoring);
  const intelNav = useSpacesNavItems('intel', FIXED_NAV_ITEMS_BY_LAYOUT_SECTION.intel);
  const dealerNav = useSpacesNavItems('dealer', FIXED_NAV_ITEMS_BY_LAYOUT_SECTION.dealer);

  const navItems = useMemo(() => {
    // Replace dynamic nav item with selected structure
    const navPaths: NavigationItem[] = BASE_NAV_PATHS.flatMap((item): NavigationItem[] => {
      if (item.path === '/portfolio') {
        // Select appropriate portfolio navigation based on PMS mode
        let portfolioNav: NavigationItem[] = PORTFOLIO_LITE_NAV;
        if (enablePMSFull) {
          portfolioNav = enablePMSPENavigationMigration ? (PORTFOLIO_NAV.children ?? []) : PORTFOLIO_LEGACY_FULL_NAV;
        }
        const newItem = { ...item, children: portfolioNav } as NavigationItem;
        return enablePMSPENavigationMigration ? [newItem] : [newItem, LEGACY_PORTFOLIO_ENGINEERING_NAV];
      }

      if (item.path === '/operations') {
        if (!enablePMSPENavigationMigration) {
          return [LEGACY_OPERATIONS_NAV];
        }
        return enablePMSFull || canAccessOperationsAsDealer(flags, isAuthorized) ? [item] : [];
      }

      if (item.path === '/research') {
        return enablePMSFull && enablePMSPENavigationMigration ? [item] : [];
      }

      return [item];
    }).map((item): NavigationItem => {
      const newItem = { ...item } as NavigationItem;
      if (item.path === '/trading') {
        newItem.children = flags.enableTradingSpaces ? (tradingNav ?? item.children) : item.children;
        if (tradingNav && flags.enableTradingSpaces) {
          newItem.enableSpaces = true;
        }
        return newItem;
      }
      if (newItem.path === '/monitoring') {
        newItem.children = flags.enableMonitoringSpaces ? (monitoringNav ?? item.children) : item.children;
        if (flags.enableMonitoringSpaces) {
          newItem.enableSpaces = true;
        }
        return newItem;
      }
      if (newItem.path === '/intel') {
        newItem.children = flags.enableSignals ? (intelNav ?? item.children) : item.children;
        if (intelNav && flags.enableSignals) {
          newItem.enableSpaces = true;
        }
        return newItem;
      }
      if (newItem.path === '/dealer') {
        newItem.children = flags.enableDealerSpaces ? (dealerNav ?? item.children) : item.children;
        if (dealerNav && flags.enableDealerSpaces) {
          newItem.enableSpaces = true;
        }
        return newItem;
      }
      return newItem;
    });

    return filterNavigationItemsByPermission(navPaths, isAuthorized, flags);
  }, [
    isAuthorized,
    flags,
    enablePMSFull,
    enablePMSPENavigationMigration,
    tradingNav,
    monitoringNav,
    intelNav,
    dealerNav,
  ]);

  return useMemo(
    () => ({
      navItems,
      topNavItems: navItems.filter(item => item.position === 'top'),
      bottomNavItems: navItems.filter(item => item.position === 'bottom'),
      userMenuItems: navItems.filter(item => item.position === 'user-menu'),
    }),
    [navItems]
  );
}
